import {
  Component,
  ElementRef,
  ViewChild,
  AfterViewInit,
  ChangeDetectorRef,
  OnInit,
  OnDestroy
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { forkJoin, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { SecurityService } from 'src/app/services';
import { Actions } from 'src/app/models';

type SettingsTab =
  | 'general'
  | 'departments'
  | 'import-previous-system'
  | 'workflow-migration'
  | 'migrate-contractors'
  | 'maps'
  | 'payment-settings'
  | 'data'
  | 'custom-lists'
  | 'email-settings'
  | 'archive-data'
  | 'contacts'
  | 'tags';

@Component({
  selector: 'wm-settings-janus',
  standalone: false,
  templateUrl: './settings-janus.component.html',
  styleUrls: ['./settings-janus.component.scss']
})
export class SettingsJanusComponent
  implements OnInit, AfterViewInit, OnDestroy {
  activeTab: SettingsTab = 'general';

  @ViewChild('tabsContainer')
  tabsContainer!: ElementRef<HTMLDivElement>;

  @ViewChild('tabContentContainer')
  tabContentContainer!: ElementRef<HTMLDivElement>;

  canScrollLeft = false;
  canScrollRight = false;
  hasOverflow = false;

  // Gated on the CLIENTS_MANAGE_DEPARTMENTS action so users without that
  // permission never see the Departments tab in the jurisdiction Settings page.
  canManageDepartments = false;

  // Gated on DO_ANYTHING: the Migrate Workflow and Migrate Contractors tools are
  // System Administrator-only utilities and their tabs must be hidden from
  // everyone else. Route guards enforce the same restriction at navigation time.
  isSysAdmin = false;

  private routerSub: Subscription;

  constructor(
    private cdr: ChangeDetectorRef,
    private router: Router,
    private route: ActivatedRoute,
    private _securitySvc: SecurityService
  ) {}

  ngOnInit(): void {
    this.updateActiveTabFromUrl(this.router.url);

    forkJoin({
      canManageDepartments: this._securitySvc.isLoginEntitled(
        Actions.CLIENTS_MANAGE_DEPARTMENTS
      ),
      isSysAdmin: this._securitySvc.isLoginEntitled(Actions.DO_ANYTHING)
    }).subscribe(({ canManageDepartments, isSysAdmin }) => {
      this.canManageDepartments = canManageDepartments;
      this.isSysAdmin = isSysAdmin;

      // The tab strip may have shrunk; recompute scroll button visibility.
      requestAnimationFrame(() => this.updateScrollButtons());
      this.cdr.detectChanges();
    });

    this.routerSub = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.updateActiveTabFromUrl(event.urlAfterRedirects);
        // Reset the content scroll back to top on tab/route change
        this.resetContentScroll();
      });
  }

  ngAfterViewInit(): void {
    this.updateScrollButtons();
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    if (this.routerSub) {
      this.routerSub.unsubscribe();
    }
  }

  updateActiveTabFromUrl(url: string) {
    if (url.includes('/settings/general')) this.activeTab = 'general';
    else if (url.includes('/settings/departments'))
      this.activeTab = 'departments';
    else if (url.includes('/settings/import-previous-system'))
      this.activeTab = 'import-previous-system';
    else if (url.includes('/settings/workflow-migration'))
      this.activeTab = 'workflow-migration';
    else if (url.includes('/settings/migrate-contractors'))
      this.activeTab = 'migrate-contractors';
    else if (url.includes('/settings/maps')) this.activeTab = 'maps';
    else if (url.includes('/settings/contacts')) this.activeTab = 'contacts';
    else if (url.includes('/settings/tags')) this.activeTab = 'tags';
    else if (url.includes('/settings/data')) this.activeTab = 'data';
    else if (url.includes('/settings/lists')) this.activeTab = 'custom-lists';
    else if (url.includes('/settings/payment-settings'))
      this.activeTab = 'payment-settings';
    else if (url.includes('/settings/email-settings'))
      this.activeTab = 'email-settings';
    else if (url.includes('/settings/archive-data'))
      this.activeTab = 'archive-data';

    this.scrollToActiveTab();
  }

  switchTab(tab: SettingsTab): void {
    this.activeTab = tab;

    const routedTabs = [
      'general',
      'departments',
      'import-previous-system',
      'workflow-migration',
      'migrate-contractors',
      'maps',
      'contacts',
      'tags',
      'data',
      'custom-lists',
      'email-settings',
      'email-footers',
      'payment-settings',
      'archive-data'
    ];
    if (routedTabs.includes(tab)) {
      if (tab === 'custom-lists') {
        this.router.navigate(['lists/list'], { relativeTo: this.route });
      } else if (tab === 'maps' || tab === 'contacts' || tab === 'data') {
        this.router.navigate([`${tab}/list`], { relativeTo: this.route });
      } else if (tab === 'payment-settings') {
        this.router.navigate(['payment-settings/payment-accounts/list'], {
          relativeTo: this.route
        });
      } else {
        this.router.navigate([tab], { relativeTo: this.route });
      }
    }

    this.scrollToActiveTab();
  }

  scrollToActiveTab() {
    requestAnimationFrame(() => {
      const el = this.tabsContainer?.nativeElement;
      if (!el) return;
      const activeEl = el.querySelector('.tab.active') as HTMLElement;

      if (activeEl) {
        activeEl.scrollIntoView({
          behavior: 'smooth',
          inline: 'center',
          block: 'nearest'
        });
      }

      this.updateScrollButtons();
      this.cdr.detectChanges();
    });
  }

  resetContentScroll() {
    if (this.tabContentContainer?.nativeElement) {
      this.tabContentContainer.nativeElement.scrollTop = 0;
    }
  }

  scrollTabs(direction: 'left' | 'right'): void {
    const el = this.tabsContainer.nativeElement;

    el.scrollBy({
      left: direction === 'right' ? 220 : -220,
      behavior: 'smooth'
    });
  }

  onTabsScroll(): void {
    this.updateScrollButtons();
    this.cdr.detectChanges();
  }

  private updateScrollButtons(): void {
    const el = this.tabsContainer?.nativeElement;
    if (!el) return;

    this.hasOverflow = el.scrollWidth > el.clientWidth;

    if (!this.hasOverflow) {
      this.canScrollLeft = false;
      this.canScrollRight = false;
      return;
    }

    this.canScrollLeft = el.scrollLeft > 0;
    this.canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
  }
}
